import React, { useContext } from 'react';
import { getSearchUrl } from '../../utils/url';
import AnalyticsEventTypes from '../../utils/analyticsEvent';
import { AnalyticsContext } from '../../context/analyticsContext';
import { StyledButton } from './styles';
import { useDynamicSearchSuggestions } from '../../utils/hooks/dynamicSearchSuggestionsHook';
import useLocale from '../../utils/hooks/localeHook';

/**
 * `VisualSearchPopularTerms` is a React component that displays a dynamic list of links.
 * Each link contains a search term fetched from the /suggestions API.
 *
 * @component
 * @param {Object} props - The properties that define the component's behavior and display.
 * @param {Object} props.experimentData - An object containing experiment data.
 * @param {string} props.experimentData.variant - The variant of the experiment.
 *
 * @returns {React.Element} A list of search terms. Each search term is a link that points to the search URL for that term.
 * If there are no search suggestions, the component returns null.
 */
const VisualSearchPopularTerms = () => {
  const locale = useLocale();
  const searchSuggestionsList = useDynamicSearchSuggestions();
  const { analyticsClient } = useContext(AnalyticsContext);

  const searchSuggestions = searchSuggestionsList.map(({ searchTerm, urlPath: path }) => {
    const url = getSearchUrl({
      locale,
      searchTerm,
      query: searchTerm,
      path,
    });

    const analyticsEventPayload = {
      props: { path: searchTerm },
      type: AnalyticsEventTypes.CLICK_POPULAR_SEARCH_SELECTION,
    };

    return { searchTerm, url, analyticsEventPayload };
  });

  return (
    <>
      {searchSuggestions.length > 0 && (
        <div
          data-testid="popular-search-terms"
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}
        >
          {searchSuggestions.map(({ searchTerm, url, analyticsEventPayload }) => (
            <StyledButton
              Component="a"
              appearance="primary"
              background="light"
              disableRipple={false}
              href={url}
              key={searchTerm}
              onClick={() => {
                analyticsClient?.clickPopularSearchSelection({
                  path: analyticsEventPayload.props.path,
                });
              }}
              size="small"
            >
              {searchTerm}
            </StyledButton>
          ))}
        </div>
      )}
    </>
  );
};

export default VisualSearchPopularTerms;
